/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import React, { useEffect, useState } from "react";
import { useMessages } from "@epic-mw/i18n";
import { CosmosText } from "@components/elements";
import { HStack, VStack } from "@panda/jsx";
import { css } from "@panda/css";
import { CosmosEnumNumberCalloutVariants, NumberCalloutFragment } from "@cosmos/hygraph-schema";

export type NumberCalloutListProps = {
	callouts: Array<NumberCalloutFragment>;
	numberCalloutVariant?: CosmosEnumNumberCalloutVariants | null;
	timeAndDate?: string | number | Date | null;
};

export const NumberCalloutList = ({
	callouts,
	numberCalloutVariant,
	timeAndDate,
}: NumberCalloutListProps): React.JSX.Element | null => {
	const messages = useMessages();
	const [countdownTime, setCountdownTime] = useState({
		days: 0,
		hours: 0,
		minutes: 0,
	});

	useEffect(() => {
		if (numberCalloutVariant !== CosmosEnumNumberCalloutVariants.CountdownDaysHoursMinutes) {
			return;
		}
		const updateCountdown = (): void => {
			const now = new Date();
			if (!timeAndDate) return;
			const then = new Date(timeAndDate);
			const diffMs = then.getTime() - now.getTime();
			if (diffMs <= 0) {
				return;
			}
			const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
			const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
			const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
			setCountdownTime({
				days: Math.max(days, 0),
				hours: Math.max(hours, 0),
				minutes: Math.max(minutes, 0),
			});
		};
		updateCountdown();
		const id = setInterval(updateCountdown, 1000 * 60);
		return () => clearInterval(id);
	}, [numberCalloutVariant, timeAndDate]);

	let finalCallouts = callouts.filter(
		(c: NumberCalloutFragment) => c.label != null || c.number != null || Boolean(c.numberDisplay),
	);

	switch (numberCalloutVariant) {
		case CosmosEnumNumberCalloutVariants.CountdownDaysHoursMinutes: {
			if (Object.values(countdownTime).every((n) => n === 0)) return null;
			finalCallouts = [
				{
					number: countdownTime.days,
					label:
						countdownTime.days === 1
							? messages["epic.cosmos.time.day"] || "Day"
							: messages["epic.cosmos.time.days"] || "Days",
				},
				{
					number: countdownTime.hours,
					label:
						countdownTime.hours === 1
							? messages["epic.cosmos.time.hour"] || "Hour"
							: messages["epic.cosmos.time.hours"] || "Hours",
				},
				{
					number: countdownTime.minutes,
					label:
						countdownTime.hours === 1
							? messages["epic.cosmos.time.minute"] || "Minute"
							: messages["epic.cosmos.time.minutes"] || "Minutes",
				},
			];
			break;
		}
	}
	// The ContentLargeImage variant matches the Easy Anti-Cheat Figma: blue
	// numbers (eds.palette.blue.400), sentence-case labels (uiMD), labels wrap
	// under the number at a constrained width so callouts stay on one row
	// beside the right-side asset. Capped at 3 callouts — the two-column layout
	// isn't designed for more, and 4+ would produce orphan dividers on wrap.
	const isContentLargeImageVariant = numberCalloutVariant === CosmosEnumNumberCalloutVariants.ContentLargeImage;
	const labelVariant = isContentLargeImageVariant ? "uiMD" : "eyebrowMD";
	const numberColor = isContentLargeImageVariant ? "eds.palette.blue.400" : undefined;
	if (isContentLargeImageVariant) {
		finalCallouts = finalCallouts.slice(0, 3);
	}

	return (
		<HStack gap="eds.dimension.32" flexWrap="wrap" alignItems="stretch">
			{finalCallouts.map((c: NumberCalloutFragment, idx: number) => (
				<React.Fragment key={idx}>
					<VStack
						gap="eds.dimension.8"
						alignItems="flex-start"
						maxWidth={isContentLargeImageVariant ? "eds.dimension.160" : undefined}
					>
						<CosmosText variant="heading2XL" text={c.numberDisplay || c.number?.toString()} color={numberColor} />
						<CosmosText variant={labelVariant} color="eds.text.secondary" text={c.label || ""} />
					</VStack>
					{idx < finalCallouts.length - 1 && (
						<div
							className={css({
								alignSelf: "stretch",
								width: "eds.dimension.1",
								background: "eds.palette.transparent.light.035",
							})}
						/>
					)}
				</React.Fragment>
			))}
		</HStack>
	);
};
